import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";

interface Dispute {
  id: string;
  status: string;
  reason: string;
  meta?: any;
  openedAt: string;
  hoursOpen: number;
  order: {
    publicId: string;
    status: string;
    customer: {
      publicId: string;
      name: string;
      phone?: string | null;
    };
  };
}

interface DisputesTableProps {
  disputes: Dispute[];
}

const STATUS_COLORS: Record<string, string> = {
  OPENED: "bg-yellow-100 text-yellow-800",
  UNDER_REVIEW: "bg-blue-100 text-blue-800",
  CUSTOMER_ACTION_REQUIRED: "bg-orange-100 text-orange-800",
  RESOLVED_DISCOUNT_APPROVED: "bg-green-100 text-green-800",
  RESOLVED_QUOTE_REVISED: "bg-green-100 text-green-800",
  RESOLVED_RETURN_APPROVED: "bg-green-100 text-green-800",
  CLOSED: "bg-gray-100 text-gray-800",
};

const RESOLUTION_LABELS: Record<string, string> = {
  DISCOUNT: "Discount",
  REVISE_QUOTE: "Revise",
  RETURN: "Return",
};

export function DisputesTable({ disputes }: DisputesTableProps) {
  if (disputes.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No disputes found
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Order</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Resolution</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>SLA</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {disputes.map((dispute) => {
            const slaColor =
              dispute.hoursOpen > 24
                ? "text-red-600"
                : dispute.hoursOpen > 12
                ? "text-orange-600"
                : "text-green-600";

            return (
              <TableRow key={dispute.id}>
                <TableCell>
                  <div className="font-mono text-sm">{dispute.order.publicId}</div>
                  <div className="text-xs text-muted-foreground">
                    {dispute.order.status}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{dispute.order.customer.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {dispute.order.customer.publicId}
                  </div>
                  {dispute.order.customer.phone && (
                    <div className="text-xs text-muted-foreground">
                      {dispute.order.customer.phone}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {RESOLUTION_LABELS[dispute.meta?.requestedResolution] ||
                      dispute.meta?.requestedResolution ||
                      "N/A"}
                  </Badge>
                </TableCell>
                <TableCell className="max-w-xs truncate">
                  {dispute.reason}
                </TableCell>
                <TableCell>
                  <Badge className={STATUS_COLORS[dispute.status] || "bg-gray-100"}>
                    {dispute.status.replace(/_/g, " ")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className={`text-sm font-medium ${slaColor}`}>
                    {dispute.hoursOpen}h
                  </span>
                  <div className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(dispute.openedAt), {
                      addSuffix: true,
                    })}
                  </div>
                </TableCell>
                <TableCell>
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/admin/disputes/${dispute.id}`}>Open</Link>
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
